import { type GridColDef } from '@mui/x-data-grid-premium';

import { type TaxCodeListItem, type TaxCodesQueryParams } from '~/data/taxCode';

import { getFormattedNumber } from '@vestigas/utils';
import i18n, { currentLocale } from '~/utils/i18n';

export type TaxCodeRow = TaxCodeListItem;

type AvailableColumn =
  | 'company'
  | keyof Pick<
      TaxCodeRow,
      'code' | 'createdOn' | 'isReverseCharge' | 'name' | 'rate'
    >;

function getColumnHeader(column: AvailableColumn) {
  return i18n.t(($) => $.table.columns[column], { ns: 'taxCodes' });
}

const dateFormatter = new Intl.DateTimeFormat(currentLocale(), {
  dateStyle: 'medium',
});

function formatDate(value: string) {
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? '' : dateFormatter.format(date);
}

/** Maps grid fields to the API's `order_by` values; fields absent here are not sortable. */
export const SORT_FIELD_TO_ORDER_BY = {
  code: 'code',
  company: 'company_name',
  createdOn: 'created_on',
  isReverseCharge: 'is_reverse_charge',
  name: 'name',
  rate: 'rate',
} as const satisfies Record<string, TaxCodesQueryParams['orderBy'] & string>;

export const columns: GridColDef<TaxCodeRow>[] = [
  {
    field: 'name',
    flex: 1,
    headerName: getColumnHeader('name'),
    minWidth: 200,
  },
  {
    align: 'right',
    field: 'rate',
    headerAlign: 'right',
    headerName: getColumnHeader('rate'),
    minWidth: 80,
    valueFormatter: (value: TaxCodeRow['rate']) =>
      `${getFormattedNumber({ value, digits: 'auto' })} %`,
  },
  {
    field: 'isReverseCharge',
    headerName: getColumnHeader('isReverseCharge'),
    minWidth: 130,
    valueFormatter: (value: TaxCodeRow['isReverseCharge']) =>
      value
        ? i18n.t(($) => $.common.yes, { ns: 'taxCodes' })
        : i18n.t(($) => $.common.no, { ns: 'taxCodes' }),
  },
  {
    field: 'code',
    headerName: getColumnHeader('code'),
    minWidth: 150,
    valueFormatter: (value: TaxCodeRow['code']) => value ?? '—',
  },
  {
    field: 'company',
    flex: 1,
    headerName: getColumnHeader('company'),
    minWidth: 180,
    valueGetter: (_value, row) => row.company.name ?? '—',
  },
  {
    align: 'right',
    field: 'createdOn',
    headerAlign: 'right',
    headerName: getColumnHeader('createdOn'),
    minWidth: 130,
    valueFormatter: (value: TaxCodeRow['createdOn']) => formatDate(value),
  },
];
